import { VERSION_STATUS } from "@/pages/configuration/versions/versionStatus";
import {
	InspectionTemplate,
	InspectionTemplateColumn,
	InspectionTemplateDraft,
} from "@/shared/models/Inspection";

export const sectionRevisions = (
	templates: InspectionTemplate[],
	sectionId: number | null,
) => {
	const all =
		sectionId === null
			? []
			: templates.filter((template) => template.SectionId === sectionId);

	const highest = all.reduce(
		(top, template) => Math.max(top, template.Rev ?? -1),
		-1,
	);

	const active = all.filter((template) => template.IsActive);

	return {
		all,
		highest,
		latest: all.find((template) => template.IsLatest) ?? null,
		current: all.find((template) => template.IsCurrentRev) ?? null,
		draft:
			active.find((template) => template.StatusCode === VERSION_STATUS.DRAFT) ??
			null,
		pending:
			active.find(
				(template) => template.StatusCode !== VERSION_STATUS.PUBLISHED,
			) ?? null,
	};
};

export type SectionRevisions = ReturnType<typeof sectionRevisions>;

export const formatRevision = (template: InspectionTemplate | null) =>
	template?.Rev == null ? "Rev pending" : `Rev ${template.Rev}`;

export const columnsOfTemplate = (
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
) =>
	columns.filter(
		(column) => column.TemplateId === template.TemplateId && column.IsActive,
	);

export const asNewColumns = (columns: InspectionTemplateColumn[]) =>
	columns.map((column) => ({
		...column,
		ColumnId: null,
		TemplateId: null,
		HasReadings: false,
	}));

export const asDraft = (
	template: InspectionTemplate,
	columns: InspectionTemplateColumn[],
): InspectionTemplateDraft => ({
	TemplateId: template.TemplateId,
	SectionId: template.SectionId,
	UploadFileId: template.UploadFileId,
	Columns: columnsOfTemplate(columns, template),
});

export const asRevisionDraft = (
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
): InspectionTemplateDraft => ({
	TemplateId: null,
	SectionId: template.SectionId,
	UploadFileId: template.UploadFileId,
	Columns: asNewColumns(columnsOfTemplate(columns, template)),
});

export const asFirstRevisionDraft = (
	sectionId: number,
): InspectionTemplateDraft => ({
	TemplateId: null,
	SectionId: sectionId,
	UploadFileId: null,
	Columns: [],
});

export const asDuplicateDraft = (
	columns: InspectionTemplateColumn[],
	template: InspectionTemplate,
): InspectionTemplateDraft => ({
	TemplateId: null,
	SectionId: null,
	UploadFileId: template.UploadFileId,
	Columns: asNewColumns(columnsOfTemplate(columns, template)),
});
